import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate
} from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { AuthProvider } from "./context/AuthContext";
import { getActivePostShortNameBase } from "./services/postSelection";
import { isPostRestrictedFromAssignedQueries } from "./services/restrictedPostsConfig";
import { SnackbarProvider } from "./components/common/Snackbar";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";
import RoleBasedRoute from "./components/RoleBasedRoute";
import PublicRoute from "./components/PublicRoute";
import DashboardRedirect from "./components/DashboardRedirect";
import MainLayout from "./components/MainLayout";
import Login from "./pages/Login";
// Manual login disabled
import ManualLogin from "./pages/ManualLogin";
import LoginFail from "./pages/LoginFail";
// import Signup from './pages/Signup';
import ForgotPassword from "./pages/ForgotPassword";
// Access pending restriction disabled — users allowed even if role not mapped.
// import ContactAdmin from './pages/ContactAdmin';
// Query pages
import AllQueries from "./pages/AllQueries";
import RaiseQuery from "./pages/RaiseQuery";
import RaiseQueryCreate from "./pages/RaiseQueryCreate";
import AssignedQueries from "./pages/AssignedQueries";
import EscalatedQueries from "./pages/EscalatedQueries";
import ClosedQueries from "./pages/ClosedQueries";
// Project pages
import Projects from "./pages/Projects";
// Admin pagesp
import Users from "./pages/Users";
import Roles from "./pages/Roles";
import EscalationMatrix from "./pages/EscalationMatrix";
// Analytics pages
import SLADashboard from "./pages/SLADashboard";
import PowerBIDashboard from "./pages/PowerBIDashboard";
// Settings pages
import Settings from "./pages/Settings";
import Help from "./pages/Help";
import { colors, borderRadius, fonts } from "./constants";

const theme = createTheme({
  palette: {
    primary: {
      main: "#667eea"
    },
    secondary: {
      main: "#764ba2"
    }
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
  },
  components: {
    // Global fix for ALL MuiOutlinedInput components (TextField, Select, Autocomplete, etc.)
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          outline: "none",
          alignItems: "center",
          fontSize: "12px",
          "& fieldset": {
            borderRadius: borderRadius.input,
            borderColor: colors.borderDefault,
            borderWidth: "1px",
            borderStyle: "solid",
            top: 0
          },
          "&:hover": {
            outline: "none",
            "& fieldset": {
              borderColor: colors.borderHover,
              borderWidth: "1px",
              borderRadius: borderRadius.input
            }
          },
          "&.Mui-focused": {
            outline: "none",
            boxShadow: "none",
            "& fieldset": {
              borderColor: colors.borderFocus,
              borderWidth: "2px",
              borderRadius: borderRadius.input
            }
          },
          "&.Mui-error": {
            outline: "none",
            "& fieldset": {
              borderColor: colors.error,
              borderRadius: borderRadius.input
            }
          },
          "&.Mui-disabled": {
            outline: "none",
            "& fieldset": {
              borderColor: colors.borderLight,
              borderRadius: borderRadius.input,
              top: 0
            }
          }
        },
        input: {
          padding: "10px 12px",
          fontSize: "12px",
          lineHeight: 1.4,
          "&::placeholder": {
            fontSize: "12px",
            color: colors.textSecondary,
            opacity: 1
          }
        },
        inputSizeSmall: {
          padding: "8px 10px"
        },
        textarea: {
          padding: "10px 12px",
          fontSize: "13px",
          lineHeight: 1.5
        }
      }
    },
    // Global fix for MuiInputBase (covers all input types)
    MuiInputBase: {
      styleOverrides: {
        root: {
          cursor: "text",
          alignItems: "center",
          "&:focus": {
            outline: "none"
          }
        },
        input: {
          cursor: "text",
          padding: "10px 12px",
          fontSize: "12px",
          lineHeight: 1.4,
          "&:focus": {
            cursor: "text"
          },
          "&::placeholder": {
            fontSize: "12px",
            color: colors.textSecondary,
            opacity: 1
          }
        }
      }
    },
    // Global fix for MuiTextField with different sizes
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: borderRadius.input,
            "& fieldset": {
              borderRadius: borderRadius.input
            }
          }
        }
      }
    },
    // Global fix for MuiSelect
    MuiSelect: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-notchedOutline": {
            borderRadius: borderRadius.input,
            top: 0
          },
          fontSize: "12px"
        },
        select: {
          padding: "10px 12px",
          fontSize: "12px",
          lineHeight: 1.4
        }
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          height: "36px",
          "&:last-child td, &:last-child th": {
            borderBottom: "none"
          }
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          paddingTop: "6px",
          paddingBottom: "6px",
          paddingLeft: "10px",
          paddingRight: "10px",
          fontSize: fonts.fontSizeXs,
          lineHeight: fonts.lineHeightTight,
          color: colors.textPrimaryDark,
          borderBottom: `1px solid ${colors.borderLight}`
        },
        head: {
          fontWeight: fonts.weightSemiBold,
          fontSize: fonts.fontSizeXs,
          letterSpacing: fonts.letterSpacingNormal,
          color: colors.textPrimaryDark
        },
        sizeSmall: {
          paddingTop: "4px",
          paddingBottom: "4px",
          paddingLeft: "8px",
          paddingRight: "8px"
        }
      }
    }
  }
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <SnackbarProvider>
          <Router>
            <Routes>
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <Login />
                  </PublicRoute>
                }
              />
              {/* Manual login disabled */}

              <Route
                path="/login/manual"
                element={
                  <PublicRoute>
                    <ManualLogin />
                  </PublicRoute>
                }
              />

              <Route
                path="/login/fail"
                element={
                  <PublicRoute>
                    <LoginFail />
                  </PublicRoute>
                }
              />
              {/*
            <Route
              path="/signup"
              element={
                <PublicRoute>
                  <Signup />
                </PublicRoute>
              }
            />
            */}
              <Route
                path="/forgot-password"
                element={
                  <PublicRoute>
                    <ForgotPassword />
                  </PublicRoute>
                }
              />
              {/* Access pending restriction disabled — users allowed even if role not mapped. */}
              {/*
            <Route
              path="/contact-admin"
              element={
                <ProtectedRoute>
                  <ContactAdmin />
                </ProtectedRoute>
              }
            />
            */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <MainLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<DashboardRedirect />} />
                {/* Query routes */}
                <Route
                  path="queries/all"
                  element={
                    <RoleBasedRoute
                      routePath="queries/all"
                      hasPermission={(user) => {
                        if (!user) return false;
                        const roleNormalized = (user.role || "")
                          .toString()
                          .trim()
                          .toLowerCase();
                        const isAdmin =
                          roleNormalized === "admin" ||
                          roleNormalized === "superadmin";

                        // Only admin can access all queries
                        return isAdmin;
                      }}
                      showUnauthorized={true}
                    >
                      <AllQueries />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="queries/raise"
                  element={
                    <RoleBasedRoute
                      routePath="queries/raise"
                      hasPermission={(user) => {
                        if (!user) return false;
                        const roleNormalized = (user.role || "")
                          .toString()
                          .trim()
                          .toLowerCase();
                        const isAdmin =
                          roleNormalized === "admin" ||
                          roleNormalized === "superadmin";
                        const isGM = roleNormalized === "gm";

                        // Block admin and GM roles from raising queries
                        if (isAdmin || isGM) return false;

                        // Allow all other roles
                        return true;
                      }}
                      showUnauthorized={true}
                    >
                      <RaiseQuery />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="queries/raise/new"
                  element={
                    <RoleBasedRoute
                      routePath="queries/raise/new"
                      hasPermission={(user) => {
                        if (!user) return false;
                        const roleNormalized = (user.role || "")
                          .toString()
                          .trim()
                          .toLowerCase();
                        const isAdmin =
                          roleNormalized === "admin" ||
                          roleNormalized === "superadmin";
                        const isGM = roleNormalized === "gm";

                        // Block admin and GM roles from raising queries
                        if (isAdmin || isGM) return false;

                        // Allow all other roles
                        return true;
                      }}
                      showUnauthorized={true}
                    >
                      <RaiseQueryCreate />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="queries/assigned"
                  element={
                    <RoleBasedRoute
                      routePath="queries/assigned"
                      hasPermission={(user) => {
                        if (!user) return false;
                        const roleNormalized = (user.role || "")
                          .toString()
                          .trim()
                          .toLowerCase();
                        const isAdmin = roleNormalized === "admin";

                        // Exclude admin only (they see all queries)
                        if (isAdmin) return false;

                        // Blocked only while the active post is configured
                        // as restricted (RESTRICTED_ASSIGNED_QUERIES_POSTS
                        // server-side), not by role — every other post
                        // (any role) can view it.
                        return !isPostRestrictedFromAssignedQueries(
                          getActivePostShortNameBase(user)
                        );
                      }}
                      showUnauthorized={true}
                    >
                      <AssignedQueries />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="queries/escalated"
                  element={
                    <RoleBasedRoute
                      routePath="queries/escalated"
                      hasPermission={(user) => {
                        if (!user) return false;
                        const roleValue =
                          user.role || user.roleName || user.userRole || "";
                        const roleNormalized = (roleValue || "")
                          .toString()
                          .trim()
                          .toLowerCase();
                        const isAdmin =
                          roleNormalized === "admin" ||
                          roleNormalized === "superadmin";

                        // Block admin users from accessing escalated queries
                        return !isAdmin;
                      }}
                      showUnauthorized={true}
                    >
                      <EscalatedQueries />
                    </RoleBasedRoute>
                  }
                />
                <Route path="queries/closed" element={<ClosedQueries />} />
                <Route
                  path="projects"
                  element={
                    <AdminRoute>
                      <Projects />
                    </AdminRoute>
                  }
                />
                {/* Admin routes */}
                <Route
                  path="admin/users"
                  element={
                    <RoleBasedRoute
                      routePath="admin/users"
                      hasPermission={(user) => {
                        if (!user) return false;
                        const roleNormalized = (user.role || "")
                          .toString()
                          .trim()
                          .toLowerCase();
                        const isAdmin =
                          roleNormalized === "admin" ||
                          roleNormalized === "superadmin";

                        // Only admin can access users management
                        return isAdmin;
                      }}
                      showUnauthorized={true}
                    >
                      <Users />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="admin/roles"
                  element={
                    <RoleBasedRoute
                      routePath="admin/roles"
                      hasPermission={(user) => {
                        if (!user) return false;
                        const roleNormalized = (user.role || "")
                          .toString()
                          .trim()
                          .toLowerCase();
                        const isAdmin =
                          roleNormalized === "admin" ||
                          roleNormalized === "superadmin";

                        // Only admin can access roles management
                        return isAdmin;
                      }}
                      showUnauthorized={true}
                    >
                      <Roles />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="admin/escalation-matrix"
                  element={<EscalationMatrix />}
                />
                {/* Analytics routes */}
                <Route
                  path="analytics/sla"
                  element={
                    <AdminRoute>
                      <SLADashboard />
                    </AdminRoute>
                  }
                />
                <Route
                  path="analytics/powerbi"
                  element={
                    <RoleBasedRoute
                      routePath="analytics/powerbi"
                      hasPermission={(user) => {
                        if (!user) return false;
                        const roleNormalized = (user.role || "")
                          .toString()
                          .trim()
                          .toLowerCase();
                        const isAdmin =
                          roleNormalized === "admin" ||
                          roleNormalized === "superadmin";
                        const isChairRole =
                          roleNormalized === "ch" ||
                          roleNormalized === "chairperson" ||
                          roleNormalized === "chairman";

                        // Allow admin, chairperson, MP, and ED roles
                        return (
                          isAdmin ||
                          isChairRole ||
                          roleNormalized === "mp" ||
                          roleNormalized === "ed"
                        );
                      }}
                      showUnauthorized={true}
                    >
                      <PowerBIDashboard />
                    </RoleBasedRoute>
                  }
                />
                {/* Settings routes */}
                <Route path="settings" element={<Settings />} />
                <Route path="help" element={<Help />} />
              </Route>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Router>
        </SnackbarProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
